import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";

import { Col, Row } from "react-bootstrap";

import { Haptics, ImpactStyle } from "@capacitor/haptics";

import Layout from "../components/layout/layout";

import { H1, P1 } from "../components/theme/text/Headings";
import {
  HomeQuizBoxIconContainer,
  HomeQuizBoxTitle,
  HomeQuizDescContainer,
} from "../components/theme/QuizBox";
import {
  ButtonGradient,
  ButtonMedium,
  ButtonOutlineMedium,
  withHaptics,
} from "../components/theme/Button";
import { DrawerLine, LineOR, ShareContainer } from "../components/theme/Drawer";

import { SwipeableDrawer } from "@mui/material";

import {
  Black,
  White08,
  DarkGrey,
  White,
  White07,
  White03,
  PageGradient,
  White05,
  Teal,
  Violet,
} from "../components/theme/colors";
import {
  ScrollContainer,
  StyledQuizBox,
  mergeQuizData,
} from "../components/theme/Carousel.js";
import remoteQuizService from "../services/RemoteQuizService";

import { db } from "../services/firebase.config";
import { useAuth } from "../services/AuthContext";
// Use centralized loading system directly
import { useLoadingManager } from "../services/LoadingManager";
import dataCache from "../services/DataCache";
import mixpanelService from "../services/MixpanelService";
import postHogService from "../services/PostHogService";

import { Clipboard } from "@capacitor/clipboard";
import QuizSocialProof from "../components/QuizSocialProof";
import QuizCompletionBox from "../components/profile/QuizCompletionBox.js";
// import DailyVibeCheckBox from "../components/theme/DailyVibeCheckBox.js";

const LinkStyle = styled(Link)`
  text-decoration: none; /* Remove underline */
  color: inherit; /* Keep the text color inherited */
`;

export const StyledLink = withHaptics(LinkStyle);

// Container to hide horizontal overflow
const SlideContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
  min-height: 100vh;
`;

// Sliding animation container
const SlidingContainer = styled.div`
  display: flex;
  width: 200%; /* Width for 2 panels */
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
  transform: translateX(
    ${(props) => (props.$activeTab === "insights" ? "-50%" : "0%")}
  );
`;

const SlidePanel = styled.div`
  width: 50%; /* Each panel takes half of the container */
  min-height: 100vh;
  flex-shrink: 0;
`;

const ContentWrapper = styled.div`
  width: 100%;
  min-height: 100%;
`;

// Category filter tabs
const CategoryTabsContainer = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 20px 5px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
`;

const CategoryTab = styled.button`
  background: ${DarkGrey};
  color: ${(props) => (props.$active ? White : White07)};
  border: none;
  border-radius: 20px;
  padding: 6px 20px;
  font-family: "Satoshi-Variable";
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  z-index: 0;

  /* Gradient border for active state */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    padding: 1.5px;
    border-radius: 20px;
    background: linear-gradient(120deg, ${Teal}, ${Violet});
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    opacity: ${(props) => (props.$active ? 1 : 0)};
    transition: opacity 0.2s ease;
  }

  &:hover::before {
    opacity: ${(props) => (props.$active ? 1 : 0.5)};
  }

  &:hover {
    background: ${(props) =>
      props.$active ? DarkGrey : "rgba(255, 255, 255, 0.08)"};
  }
`;

const Home = () => {
  const { currentUserState } = useAuth();
  const loadingManager = useLoadingManager();

  // Tab management
  const [activeTab, setActiveTab] = useState("quiz");

  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [startQuizURL, setStartQuizURL] = useState(null);
  const [sendQuizURL, setSendQuizURL] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [open, setOpen] = useState(false);

  // Dynamic quiz state objects (support any quiz, not just hardcoded 8)
  const [quizCompleted, setQuizCompleted] = useState({});
  const [quizInProgress, setQuizInProgress] = useState({});
  const [quizAnsweredCount, setQuizAnsweredCount] = useState({});
  const [quizIds, setQuizIds] = useState({});
  const [userProfile, setUserProfile] = useState(null);

  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // State for quiz list from database
  const [quizList, setQuizList] = useState([]);

  // Category filter state
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [categoryFilters, setCategoryFilters] = useState([]);

  // Filter quizzes by category (must be defined before useEffects that use it)
  const filteredQuizList =
    categoryFilter === "all"
      ? quizList
      : quizList.filter((quiz) => {
          // Match if category equals filter OR subcategory equals filter
          return (
            quiz.category === categoryFilter ||
            quiz.subcategory === categoryFilter
          );
        });

  // Add loading state guard to prevent infinite loop during initial load
  const loadingRef = useRef(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Fetch remote quiz list and categories on mount
  useEffect(() => {
    const fetchRemoteData = async () => {
      try {
        // Fetch quizzes and categories in parallel
        const [remoteList, remoteCategories] = await Promise.all([
          remoteQuizService.getQuizListForDisplay(),
          remoteQuizService.getCategoryFilters(),
        ]);

        if (remoteList && remoteList.length > 0) {
          const merged = mergeQuizData(remoteList);
          setQuizList(merged);
        }

        if (remoteCategories && remoteCategories.length > 0) {
          setCategoryFilters(remoteCategories);
        } else {
          console.log(
            "📦 No categories loaded, remoteCategories:",
            remoteCategories
          );
        }
      } catch (error) {
        console.log("Error fetching remote data:", error);
      }
    };

    fetchRemoteData();
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    const scrollLeft = container.scrollLeft;
    const cardWidth = container.querySelector("div")?.offsetWidth + 20;

    const newIndex = Math.round(scrollLeft / cardWidth);

    if (newIndex !== activeIndex) {
      setActiveIndex(newIndex);
      Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    }
  }, [activeIndex]);

  const toggleDrawer = useCallback(
    async (state, quiz, url) => {
      const isNewQuiz = selectedQuiz !== quiz;

      // Track quiz box interaction
      if (state && isNewQuiz) {
        mixpanelService.track("Quiz Box Clicked", {
          quiz_type: quiz,
          page: "Home",
          user_id: currentUserState?.uid || "anonymous",
          timestamp: new Date().toISOString(),
        });
      }

      if (!isNewQuiz && open) {
        // Same quiz clicked while drawer is open - close it
        setOpen(false);
      } else {
        // New quiz or drawer was closed - open it
        setSelectedQuiz(quiz);
        setStartQuizURL(url);
        setOpen(true);
      }
    },
    [selectedQuiz, currentUserState, open]
  );

  const handleClose = useCallback(() => setOpen(false), []);
  const handleOpen = useCallback(() => setOpen(true), []);

  // Keep selected quiz in sync with the carousel's activeIndex
  useEffect(() => {
    const quiz = filteredQuizList[activeIndex];
    if (!quiz) return;
    if (selectedQuiz !== quiz.id) {
      setSelectedQuiz(quiz.id);
      setStartQuizURL(quiz.startURL);
    }
  }, [activeIndex, selectedQuiz, filteredQuizList]);

  // Memoize the load function to prevent recreating on every render
  const loadHomeData = useCallback(async () => {
    if (!currentUserState) return;

    // Prevent concurrent calls during initial load
    if (loadingRef.current) {
      console.log("🔄 Skipping loadHomeData - already in progress");
      return;
    }

    loadingRef.current = true;

    const homeLoadingKey = `home-data-${currentUserState.uid}`;

    try {
      const result = await loadingManager.loadWithCache(
        homeLoadingKey,
        async () => {
          // Load profile data
          const profileKey = `user-profile-${currentUserState.uid}`;
          let profileData = dataCache.get(profileKey);

          if (!profileData) {
            const profileDocRef = doc(
              db,
              "public-profiles",
              currentUserState.uid
            );
            const profileDoc = await getDoc(profileDocRef);
            profileData = profileDoc.exists() ? profileDoc.data() : null;

            if (profileData) {
              dataCache.set(profileKey, profileData, 5 * 60 * 1000);
            }
          }

          if (!profileData) return null;

          // Capture displayName for share URL encoding
          const profileDisplayName = profileData.displayName || "Someone";

          // Get all quiz configs from remote
          const allQuizConfigs = await remoteQuizService.getAllQuizConfigs();
          if (!allQuizConfigs) {
            console.log("Could not fetch quiz configs");
            return null;
          }

          // Map of databaseField to quizType for profile lookups
          const databaseFieldMap = {};
          for (const [quizType, config] of Object.entries(allQuizConfigs)) {
            if (config.databaseField) {
              databaseFieldMap[config.databaseField] = quizType;
            }
          }

          // Helper to check quiz status by quizId
          const checkQuizStatusById = async (quizId, quizType) => {
            if (!quizId)
              return {
                completed: false,
                inProgress: false,
                answeredCount: 0,
                quizId: null,
              };

            const cacheKey = `quiz-completion-${quizType}-${currentUserState.uid}`;
            let cached = dataCache.get(cacheKey);

            if (!cached) {
              const docRef = doc(db, "quiz-answers", quizId);
              const docSnap = await getDoc(docRef);

              if (docSnap.exists()) {
                const data = docSnap.data();
                const completed = data.status === "completed";
                const answeredCount = data.answers
                  ? Object.keys(data.answers).length
                  : 0;
                const inProgress = !completed && answeredCount > 0;
                cached = { completed, inProgress, answeredCount, quizId };
              } else {
                cached = {
                  completed: false,
                  inProgress: false,
                  answeredCount: 0,
                  quizId,
                };
              }
              dataCache.set(cacheKey, cached, 3 * 60 * 1000);
            }
            return cached;
          };

          // Helper to check quiz status by querying quiz-answers collection
          const checkQuizStatusByQuery = async (quizType) => {
            const cacheKey = `quiz-completion-${quizType}-${currentUserState.uid}`;
            let cached = dataCache.get(cacheKey);

            if (!cached) {
              const q = query(
                collection(db, "quiz-answers"),
                where("uid", "==", currentUserState.uid),
                where("quizName", "==", quizType)
              );
              const snap = await getDocs(q);
              let latestId = null;
              let latestCreated = 0;
              let latestStatus = null;
              let latestAnswers = null;
              snap.forEach((d) => {
                const data = d.data();
                const created = data.created?.toMillis?.() || 0;
                if (created >= latestCreated) {
                  latestCreated = created;
                  latestId = d.id;
                  latestStatus = data.status || null;
                  latestAnswers = data.answers || null;
                }
              });
              const completed = latestStatus === "completed";
              const answeredCount = latestAnswers
                ? Object.keys(latestAnswers).length
                : 0;
              const inProgress = !completed && answeredCount > 0;
              cached = {
                completed,
                inProgress,
                answeredCount,
                quizId: latestId,
              };
              dataCache.set(cacheKey, cached, 3 * 60 * 1000);
            }
            return cached;
          };

          // Check status for all quizzes dynamically
          const quizStatusPromises = Object.entries(allQuizConfigs).map(
            async ([quizType, config]) => {
              // First check if quiz ID is stored on profile
              const profileQuizId = config.databaseField
                ? profileData[config.databaseField]
                : null;

              let status;
              if (profileQuizId) {
                // Quiz ID is on profile, use it directly
                status = await checkQuizStatusById(profileQuizId, quizType);
              } else {
                // Query quiz-answers collection for this quiz type
                status = await checkQuizStatusByQuery(quizType);
              }

              return { quizType, ...status };
            }
          );

          const quizStatuses = await Promise.all(quizStatusPromises);

          // Build result objects
          const completedMap = {};
          const inProgressMap = {};
          const answeredCountMap = {};
          const quizIdMap = {};

          quizStatuses.forEach(
            ({ quizType, completed, inProgress, answeredCount, quizId }) => {
              completedMap[quizType] = completed;
              inProgressMap[quizType] = inProgress;
              answeredCountMap[quizType] = answeredCount;
              quizIdMap[quizType] = quizId;
            }
          );

          return {
            displayName: profileDisplayName,
            completedMap,
            inProgressMap,
            answeredCountMap,
            quizIdMap,
            profileData,
          };
        },
        homeLoadingKey,
        "NORMAL",
        { cacheTTL: 3 * 60 * 1000 }
      );

      if (result) {
        setQuizCompleted(result.completedMap);
        setQuizInProgress(result.inProgressMap);
        setQuizAnsweredCount(result.answeredCountMap);
        setQuizIds(result.quizIdMap);
        setDisplayName(result.displayName);
        setUserProfile(result.profileData);
        setInitialLoadComplete(true);
      }
    } catch (error) {
      console.error("Error loading home data:", error);
    } finally {
      loadingRef.current = false;
    }
  }, [currentUserState, loadingManager]);

  useEffect(() => {
    // Only run if not already loading and not already completed
    if (!initialLoadComplete && !loadingRef.current) {
      loadHomeData();
    }
  }, [loadHomeData, initialLoadComplete]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to top when switching to quiz tab
  useEffect(() => {
    if (activeTab === "quiz") {
      window.scrollTo(0, 0);
    }
  }, [activeTab]);

  // Dynamically update the sendQuizURL based on the selected quiz (supports any quiz)
  useEffect(() => {
    if (!currentUserState || !selectedQuiz) {
      setSendQuizURL(null);
      return;
    }

    const quizId = quizIds[selectedQuiz];
    if (!quizId) {
      setSendQuizURL(null);
      return;
    }

    const encode = (s) => encodeURIComponent(s || "Someone");
    const namePart = encode(displayName);
    const shareUrl = `/s/${selectedQuiz}/${namePart}?from=${quizId}`;
    setSendQuizURL(shareUrl);
  }, [currentUserState, selectedQuiz, quizIds, displayName]);

  // Refresh completion state whenever the selected quiz changes (e.g., by carousel scroll)
  useEffect(() => {
    let isCancelled = false;
    const refresh = async () => {
      if (!selectedQuiz) return;

      const selectedQuizId = quizIds[selectedQuiz];
      if (!selectedQuizId) return;

      try {
        const docRef = doc(db, "quiz-answers", selectedQuizId);
        const docSnap = await getDoc(docRef);
        if (isCancelled) return;

        const completed =
          docSnap.exists() && docSnap.data().status === "completed";

        // Update the dynamic state
        setQuizCompleted((prev) => ({ ...prev, [selectedQuiz]: completed }));
      } catch (e) {
        console.warn("Failed to refresh selected quiz completion:", e);
      }
    };
    refresh();
    return () => {
      isCancelled = true;
    };
  }, [selectedQuiz, quizIds]);

  const copyToClipboard = useCallback(
    async (URL) => {
      try {
        await Clipboard.write({ string: URL });
        alert("Link copied to clipboard!");

        // Track quiz link copied
        if (selectedQuiz) {
          const quiz = quizList.find((q) => q.id === selectedQuiz);
          const linkCopiedProps = {
            quiz_type: selectedQuiz,
            quiz_name: quiz?.title || selectedQuiz,
            source: "home",
            url: URL,
            user_id: currentUserState?.uid || "anonymous",
            timestamp: new Date().toISOString(),
          };
          mixpanelService.track("Quiz Link Copied", linkCopiedProps);
          postHogService.track("Quiz Link Copied", linkCopiedProps);
        }
      } catch (err) {
        console.error("Clipboard copy failed:", err);
      }
    },
    [selectedQuiz, currentUserState, quizList]
  );

  const handleTabChange = useCallback(
    (tab) => {
      setActiveTab(tab);

      // Track tab switch
      const tabSwitchProps = {
        tab: tab,
        page: "Home",
        user_id: currentUserState?.uid || "anonymous",
        timestamp: new Date().toISOString(),
      };
      mixpanelService.track("Home Tab Switch", tabSwitchProps);
      postHogService.track("Home Tab Switch", tabSwitchProps);

      // Scroll to top when switching to quiz tab
      if (tab === "quiz") {
        window.scrollTo(0, 0);
      }
    },
    [currentUserState]
  );

  const handleQuizStart = useCallback(
    (isRetake = false) => {
      if (!selectedQuiz) return;

      // Get quiz name from quizList
      const quiz = quizList.find((q) => q.id === selectedQuiz);

      // Track quiz start
      const quizStartProps = {
        quiz_type: selectedQuiz,
        quiz_name: quiz?.title || selectedQuiz,
        is_retake: isRetake,
        page: "Home",
        user_id: currentUserState?.uid || "anonymous",
        timestamp: new Date().toISOString(),
      };
      mixpanelService.track("Quiz Started", quizStartProps);
      postHogService.track("Quiz Started", quizStartProps);
    },
    [selectedQuiz, currentUserState, quizList]
  );

  // Helper to get the route prefix for a quiz based on its startURL from the database
  const getQuizRoutePrefix = useCallback(
    (quizId) => {
      const quiz = quizList.find((q) => q.id === quizId);
      if (!quiz?.startURL) return `/quiz/${quizId}`;
      // If startURL starts with /quiz/, use that pattern, otherwise use legacy pattern
      if (quiz.startURL.startsWith("/quiz/")) {
        return `/quiz/${quizId}`;
      }
      return `/${quizId}`;
    },
    [quizList]
  );

  // Helper to get in-progress state and resume URL for selected quiz (dynamic)
  const getQuizResumeInfo = useCallback(() => {
    if (!selectedQuiz) return { isInProgress: false, resumeURL: null };

    const isInProgress = quizInProgress[selectedQuiz] || false;
    const answeredCount = quizAnsweredCount[selectedQuiz] || 0;

    // Calculate resume URL - next question is answeredCount + 1
    const nextQuestion = answeredCount + 1;
    const routePrefix = getQuizRoutePrefix(selectedQuiz);
    const resumeURL = isInProgress
      ? `${routePrefix}/question/${nextQuestion}`
      : null;

    return { isInProgress, resumeURL, answeredCount };
  }, [selectedQuiz, quizInProgress, quizAnsweredCount, getQuizRoutePrefix]);

  const { isInProgress: selectedQuizInProgress, resumeURL } =
    getQuizResumeInfo();

  // Get gradient colors for the selected quiz from quizList (comes from Firebase)
  const selectedQuizColors = useMemo(() => {
    if (!selectedQuiz) return { background: null, transitionBackground: null };
    const quiz = quizList.find((q) => q.id === selectedQuiz);
    return {
      background: quiz?.background || null,
      transitionBackground: quiz?.transitionBackground || null,
    };
  }, [selectedQuiz, quizList]);

  // Calculate category counts dynamically based on available category filters
  const categoryCounts = useMemo(() => {
    // Initialize counts dynamically from categoryFilters
    const counts = { all: quizList.length };
    categoryFilters.forEach((filter) => {
      if (filter.id !== "all") {
        counts[filter.id] = 0;
      }
    });

    quizList.forEach((quiz) => {
      // Count by category
      if (quiz.category && counts[quiz.category] !== undefined) {
        counts[quiz.category]++;
      }
      // Also count by subcategory (if different from category)
      if (
        quiz.subcategory &&
        quiz.subcategory !== quiz.category &&
        counts[quiz.subcategory] !== undefined
      ) {
        counts[quiz.subcategory]++;
      }
    });

    return counts;
  }, [quizList, categoryFilters]);

  // Handle category filter change
  const handleCategoryChange = useCallback((category) => {
    setCategoryFilter(category);
    setActiveIndex(0);
    setOpen(false); // Close drawer when changing filters
    // Reset scroll position
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
    }
  }, []);

  return (
    <Layout
      activeTab={activeTab}
      onTabChange={handleTabChange}
      showTabs={true}
      $background={PageGradient}
      $navbarBackground={Black}
    >
      {userProfile && (
        <QuizCompletionBox
          userProfile={userProfile}
          style={{ margin: "10px 20px 25px" }}
        />
      )}
      <Col
        lg={{ offset: 4, span: 4 }}
        md={{ offset: 3, span: 6 }}
        sm={{ offset: 2, span: 8 }}
        xs={{ span: 12 }}
        className="text-align-center"
      >
        <SlideContainer>
          <SlidingContainer $activeTab={activeTab}>
            {/* Quiz Content Panel */}
            <SlidePanel>
              <ContentWrapper>
                {categoryFilters.length > 0 && (
                  <CategoryTabsContainer>
                    {categoryFilters.map((filter) => (
                      <CategoryTab
                        key={filter.id}
                        $active={categoryFilter === filter.id}
                        onClick={() => handleCategoryChange(filter.id)}
                      >
                        {filter.label}
                        {categoryCounts[filter.id] > 0 && (
                          <span
                            style={{
                              marginLeft: "2.5px",
                              color:
                                categoryFilter === filter.id
                                  ? White05
                                  : White03,
                            }}
                          >
                            {" "}
                            {categoryCounts[filter.id]}
                          </span>
                        )}
                      </CategoryTab>
                    ))}
                  </CategoryTabsContainer>
                )}
                <ScrollContainer ref={scrollRef} onScroll={handleScroll}>
                  {filteredQuizList.map((quiz) => (
                    <StyledQuizBox
                      key={quiz.id}
                      $selected={selectedQuiz === quiz.id}
                      $anyselected={selectedQuiz !== null}
                      onClick={() => toggleDrawer(true, quiz.id, quiz.startURL)}
                    >
                      <HomeQuizBoxTitle
                        $background={quiz.background}
                        $transitionBackground={quiz.transitionBackground}
                      >
                        <HomeQuizBoxIconContainer
                          $background={quiz.background}
                          $transitionBackground={quiz.transitionBackground}
                        >
                          {quiz.icon}
                        </HomeQuizBoxIconContainer>
                        <H1 className="mt-15">{quiz.title}</H1>
                      </HomeQuizBoxTitle>
                      <HomeQuizDescContainer>
                        <P1 color={White08} style={{ fontWeight: 600 }}>
                          {quiz.description}
                        </P1>
                      </HomeQuizDescContainer>
                      <QuizSocialProof quizType={quiz.id} />
                    </StyledQuizBox>
                  ))}
                </ScrollContainer>

                {/* <DailyVibeCheckBox /> */}

                <SwipeableDrawer
                  open={open}
                  onClose={handleClose}
                  onOpen={handleOpen}
                  anchor="bottom"
                  disableSwipeToOpen={true}
                  swipeAreaWidth={0}
                  hideBackdrop={true}
                  ModalProps={{
                    keepMounted: false,
                    sx: {
                      pointerEvents: "none",
                    },
                  }}
                  PaperProps={{
                    sx: {
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      maxHeight: "90vh",
                      pointerEvents: "auto",
                    },
                  }}
                  sx={{
                    zIndex: 1300,
                  }}
                >
                  <div
                    style={{
                      background: DarkGrey,
                      borderRadius: "30px 30px 0 0",
                      boxShadow: "none",
                      overflow: "hidden",
                      pointerEvents: "auto",
                    }}
                  >
                    <Row className="text-align-center">
                      <div className="justify-content-center">
                        <DrawerLine />
                      </div>

                      <div style={{ padding: "10px 30px 40px" }}>
                        <ShareContainer>
                          {quizCompleted[selectedQuiz] ? (
                            <>
                              <P1 className="mb-15">Copy link and share</P1>

                              <ButtonGradient
                                className="mb-10"
                                width="95%"
                                style={{ marginRight: 0 }}
                                $background={selectedQuizColors.background}
                                $transitionBackground={
                                  selectedQuizColors.transitionBackground
                                }
                                onClick={() =>
                                  copyToClipboard(
                                    `https://www.vibecheckme.com${sendQuizURL}`
                                  )
                                }
                              >
                                Share!
                              </ButtonGradient>
                            </>
                          ) : (
                            <>
                              <P1>
                                {selectedQuizInProgress
                                  ? "Continue where you left off"
                                  : "Step 1: Take the quiz"}
                              </P1>
                              <Link
                                to={
                                  selectedQuizInProgress
                                    ? resumeURL
                                    : startQuizURL
                                }
                                onClick={() => handleQuizStart(false)}
                              >
                                <ButtonGradient
                                  className="mt-10 mb-10"
                                  width="330px"
                                  style={{ marginRight: 0 }}
                                  $background={selectedQuizColors.background}
                                  $transitionBackground={
                                    selectedQuizColors.transitionBackground
                                  }
                                >
                                  {selectedQuizInProgress
                                    ? "Resume Quiz"
                                    : "Get Started"}
                                </ButtonGradient>
                              </Link>
                            </>
                          )}
                        </ShareContainer>
                        {quizCompleted[selectedQuiz] && (
                          <>
                            <LineOR>or</LineOR>
                            <ShareContainer>
                              <div className="display-flex display-column">
                                <Link
                                  to={`${getQuizRoutePrefix(
                                    selectedQuiz
                                  )}/results`}
                                >
                                  <ButtonMedium
                                    width="300px"
                                    style={{ marginRight: 0 }}
                                    $background={selectedQuizColors.background}
                                  >
                                    My Results
                                  </ButtonMedium>
                                </Link>
                                <Link
                                  to={startQuizURL}
                                  onClick={() => handleQuizStart(true)}
                                >
                                  <ButtonOutlineMedium
                                    width="300px"
                                    className="mt-10"
                                    style={{ marginRight: 0 }}
                                    $background={selectedQuizColors.background}
                                    $textHoverOverride={DarkGrey}
                                  >
                                    Re-Take Quiz
                                  </ButtonOutlineMedium>
                                </Link>
                              </div>
                            </ShareContainer>
                          </>
                        )}
                      </div>
                    </Row>
                  </div>
                </SwipeableDrawer>
              </ContentWrapper>
            </SlidePanel>

            {/* Second Tab Panel - Placeholder for future content */}
            <SlidePanel>
              <ContentWrapper>
                {/* This panel is reserved for future use */}
              </ContentWrapper>
            </SlidePanel>
          </SlidingContainer>
        </SlideContainer>
      </Col>
    </Layout>
  );
};

export default Home;
